import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { RenderMarkdownWithLatex } from '../../utils/utils';
import { useWindowSize } from '../../hooks/useWindowSize';
import { 
    AiOutlineQuestionCircle, 
    AiOutlineArrowLeft,
    AiOutlineCheckCircle,
    AiOutlineCloseCircle, 
    AiOutlineExclamationCircle,
    AiOutlineSync
} from "react-icons/ai"; 
import { FaLock, FaPencilAlt, FaRegLightbulb, FaRegFileAlt } from 'react-icons/fa';

// Importy komponentów UI (takie same jak w ExamSheetResolver)
import PageTestPage from '../molecules/PageTestPage';
import AiChatPopup from '../molecules/AiChatPopup';
import TrainingProblemsModal from '../molecules/TrainingProblemsModal';
import AttemptSummaryModal from '../molecules/AttemptSummaryModal';
import PaywallPopup from "../molecules/PaywallPopup"; 
import SolutionStepper from "../molecules/SolutionStepper";
import HintAndFormulas from "../molecules/HintAndFormulas";
import ScratchpadModal from "../molecules/ScratchpadModal";
import HintPopup from "../molecules/HintPopup";

// --- DANE MOCKOWE (Twoje zadania z poprawioną składnią JS) ---
const TUTORIAL_DATA = {
    attempt_id: "tutorial_123",
    sheet_name: "Arkusz Pokazowy",
    status: "in_progress",
    problems: [
        {
            problem_id: 103528,
            field_id: 7,
            subfield_id: 131,
            problem_number: 1,
            display_number: "1",
            is_excluded: false,
            is_open: false,
            max_points: "1.00",
            question: "Liczby $x_{1}$ i $x_{2}$ są różnymi rozwiązaniami równania $|x-6|=4$. Iloczyn $x_{1}\\cdot x_{2}$ jest równy",
            image_link: "",
            hint: "Rozwiąż równanie. Pamiętaj o dwóch przypadkach dla wartości bezwzględnej. Pomnóż wyniki.",
            solution: "Krok 1: ### 🔹 Rozbicie na przypadki\r\nRównanie $$|x - 6| = 4$$ oznacza, że odległość $$x$$ od $$6$$ wynosi $$4$$.\r\nAlgebraicznie zapisujemy to jako:\r\n$$x - 6 = 4 \\quad \\text{lub} \\quad x - 6 = -4$$\r\n\r\nKrok 2: ### 🔹 Wyznaczenie rozwiązań\r\nPrzenosimy $$-6$$ na drugą stronę równania (jako $$+6$$).\r\n1. $$x = 4 + 6 \\Rightarrow x_1 = 10$$\r\n2. $$x = -4 + 6 \\Rightarrow x_2 = 2$$\r\n\r\nKrok 3: ### 🔹 Iloczyn\r\n$$10 \\cdot 2 = 20$$",
            true_answer: "20",
            answers: ["100", "20", "24", "4"],
            criteria: [],
            linked_formulas: [
                {
                    matura_formula_match_id: 7533,
                    name: "Definicja wartości bezwzględnej",
                    text: "$|x|=\\\\begin{cases} x & \\text{dla } x \\ge 0 \\\\\\\\ -x & \\text{dla } x < 0 \\\\end{cases}$"
                }
            ],
            // Fake AI response
            ai_explanation: "W tym zadaniu korzystamy z definicji wartości bezwzględnej. Odległość liczby x od 6 wynosi 4. Zatem x może być równe 6+4 lub 6-4."
        },
        {
            problem_id: 103536,
            field_id: 8,
            subfield_id: 204,
            problem_number: 9,
            display_number: "2",
            is_excluded: false,
            is_open: true,
            max_points: "2.00",
            question: "Rozwiąż nierówność $x(x+4)<x-2$. Zapisz obliczenia.",
            image_link: "",
            hint: "Uporządkuj nierówność. Otrzymasz bardzo prosty trójmian, w którym łatwo policzyć deltę lub zgrupować wyrazy.",
            solution: "Krok 1: ### 🔹 Przekształcenia\r\n$$x(x+4) < x - 2$$\r\n$$x^2 + 4x < x - 2$$\r\nPrzenosimy na lewo:\r\n$$x^2 + 4x - x + 2 < 0$$\r\n$$x^2 + 3x + 2 < 0$$\r\n\r\nKrok 2: ### 🔹 Metoda 1: Delta\r\n> $$\\Delta = b^2 - 4ac$$\r\n\r\n$$\\Delta = 3^2 - 4 \\cdot 1 \\cdot 2 = 9 - 8 = 1$$\r\n\r\n> $$x_1=\\frac{-b-\\sqrt{\\Delta}}{2a}, \\quad x_2=\\frac{-b+\\sqrt{\\Delta}}{2a}$$\r\n\r\n$$x_1 = \\frac{-3 - 1}{2} = -2$$\r\n$$x_2 = \\frac{-3 + 1}{2} = -1$$\r\n\r\nKrok 3: ### 🔹 Metoda 2: Grupowanie\r\nRozbijamy $$3x$$ na $$x + 2x$$:\r\n$$x^2 + x + 2x + 2 < 0$$\r\n$$x(x+1) + 2(x+1) < 0$$\r\n$$(x+1)(x+2) < 0$$\r\nMiejsca zerowe: $$-1$$ i $$-2$$.\r\n\r\nKrok 4: ### 🔹 Wynik\r\nNierówność ostra ($$<0$$), przedział otwarty między pierwiastkami.\r\n\r\n$$x \\in (-2, -1)$$\r\n",
            true_answer: "x \\in (-2, -1)",
            answers: ["x \\in (-2, -1)"],
            criteria: [
                { description: "Obliczenie miejsc zerowych nierówności: $x_1=-2$ oraz $x_2=-1$.", points: "1.00" },
                { description: "Zapisanie zbioru rozwiązań nierówności: przedział $(-2, -1)$.", points: "1.00" }
            ],
            linked_formulas: [
                { matura_formula_match_id: 7543, name: "Wyróżnik ($\\Delta$)", text: "$\\Delta = b^2 - 4ac$" }
            ],
            ai_explanation: "To klasyczna nierówność kwadratowa. Najpierw wymnóż nawias po lewej, potem przenieś wszystko na jedną stronę, żeby po prawej było 0. Policz deltę i miejsca zerowe."
        },
        {
            problem_id: 103538,
            field_id: 12,
            subfield_id: 504,
            problem_number: 11,
            display_number: "3",
            is_excluded: false,
            is_open: false,
            max_points: "1.00",
            question: "Funkcja liniowa f jest określona wzorem $f(x)=\\frac{1}{2}x-k,$ gdzie k jest liczbą rzeczywistą. Miejsce zerowe funkcji f jest liczbą większą od 2. Liczba k należy do przedziału",
            image_link: "",
            hint: "Miejsce zerowe to taki $x$, dla którego **igreki się zerują** ($f(x)=0$). Wylicz $x$ z równania (będzie zależał od $k$), a potem sprawdź, kiedy ten wynik będzie większy od 2.",
            solution: "Krok 1: ### 🔹 Wyznaczamy wzór na miejsce zerowe\r\nSzukamy miejsca zerowego, więc przyrównujemy wzór funkcji do zera:\r\n$$\\frac{1}{2}x - k = 0$$\r\n\r\nTeraz musimy wyciągnąć z tego iksa. Przenosimy $k$ na prawo i mnożymy wszystko razy 2:\r\n$$\\frac{1}{2}x = k \\quad /\\cdot 2$$\r\n$$x = 2k$$\r\n\r\nKrok 2: ### 🔹 Warunek z zadania\r\nW treści zadania jest powiedziane wprost: miejsce zerowe ma być **większe od 2**.\r\nPodstawiamy nasz wynik ($2k$) i zapisujemy nierówność:\r\n$$2k > 2$$\r\n\r\nKrok 3: ### 🔹 Wynik\r\nDzielimy nierówność przez 2 i mamy gotową odpowiedź:\r\n$$k > 1$$\r\nCzyli $k$ należy do przedziału $(1, +\\infty)$.",
            true_answer: "$(1,+\\infty)$",
            answers: ["$(0,1)$", "$(1,+\\infty)$", "$(-1,0)$", "$(-\\infty,-1)$"],
            criteria: [],
            linked_formulas: [],
            ai_explanation: "Miejsce zerowe to argument, dla którego wartość funkcji wynosi 0. Podstaw 0 za f(x) i wyznacz x. Otrzymasz x = 2k. Skoro miejsce zerowe ma być większe od 2, to 2k > 2."
        }
    ]
};

// --- ANIMACJE ---
const problemTransitionVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.3, ease: "easeOut" } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.2, ease: "easeIn" } }
};

// --- KOMPONENT STATUSU (Kopia 1:1) ---
const ProblemStatusBadge = ({ problem, answer }) => {
    const baseClass = "flex justify-center items-center gap-2 text-sm font-semibold py-2";

    if (!answer) {
        return (
            <div className={`${baseClass} text-gray-400`}>
                <AiOutlineQuestionCircle className="w-4 h-4" />
                <span>Oczekuje na rozwiązanie</span>
            </div>
        );
    }

    if (answer.is_skipped) {
        return (
            <div className={`${baseClass} text-red-600`}>
                <AiOutlineCloseCircle className="w-4 h-4" />
                <span>Pominięto (0 pkt)</span>
            </div>
        );
    }

    const maxPts = parseInt(problem.max_points, 10); 
    if (problem.is_open) {
        const awardedPts = parseInt(answer.points_awarded, 10);
        if (awardedPts === maxPts) {
            return (
                <div className={`${baseClass} text-green-600`}>
                    <AiOutlineCheckCircle className="w-4 h-4" />
                    <span>Rozwiązane ({awardedPts} / {maxPts} pkt)</span>
                </div>
            );
        }
        if (awardedPts > 0) {
            return (
                <div className={`${baseClass} text-yellow-600`}>
                    <AiOutlineExclamationCircle className="w-4 h-4" />
                    <span>Częściowo rozwiązane ({awardedPts} / {maxPts} pkt)</span>
                </div>
            );
        }
        return (
            <div className={`${baseClass} text-red-600`}>
                <AiOutlineCloseCircle className="w-4 h-4" />
                <span>Nieprawidłowo rozwiązane (0 / {maxPts} pkt)</span>
            </div>
        );
    } 
    else {
        if (answer.selected_answer === problem.true_answer) {
             return (
                <div className={`${baseClass} text-green-600`}>
                    <AiOutlineCheckCircle className="w-4 h-4" />
                    <span>Poprawna odpowiedź (1 pkt)</span>
                </div>
            );
        } 
        else {
             return (
                <div className={`${baseClass} text-red-600`}>
                    <AiOutlineCloseCircle className="w-4 h-4" />
                    <span>Nieprawidłowa odpowiedź (0 pkt)</span>
                </div>
            );
        }
    }
};

const TutorialExamResolver = () => {
    const navigate = useNavigate();
    const { width } = useWindowSize();

    // --- LOCAL STATE (zamiast API) ---
    // Inicjalizujemy głęboką kopią, żeby zmiany nie psuły stałej przy przeładowaniu komponentu
    const [attemptData, setAttemptData] = useState(JSON.parse(JSON.stringify(TUTORIAL_DATA)));
    const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
    const startTimeRef = useRef(Math.floor(Date.now() / 1000));
    
    // Stany UI identyczne jak w oryginale
    const [isHintUsed, setIsHintUsed] = useState(false);
    const [showSolution, setShowSolution] = useState(false);
    const [selectedAnswerText, setSelectedAnswerText] = useState(null);
    // Mockowane dane usera
    const [accountStatus] = useState({ has_access: true, problems_daily_goal: 5, problems_solved_today: 2 });
    const [popupState, setPopupState] = useState({ isOpen: false, completionLevel: 0 });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [addTopicStatus, setAddTopicStatus] = useState({ loading: false, added: false });
    const [tempPoints, setTempPoints] = useState('');
    const [summaryAttemptId, setSummaryAttemptId] = useState(null);
    const [isNavigating, setIsNavigating] = useState(false);

    // Modale narzędziowe
    const [sketches, setSketches] = useState({});
    const [isScratchpadOpen, setIsScratchpadOpen] = useState(false);
    const [isHintPopupOpen, setIsHintPopupOpen] = useState(false);

    // AI Chat Mock State
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatHistory, setChatHistory] = useState([]);
    const [isResponding, setIsResponding] = useState(false);

    // Zmienne pochodne
    const currentProblem = attemptData.problems[currentProblemIndex];
    const savedAnswer = currentProblem?.attempt_answer;
    const isAnswerChecked = !!savedAnswer;
    const isReviewMode = attemptData?.status === 'completed';

    // Reset przy zmianie pytania
    useEffect(() => {
        startTimeRef.current = Math.floor(Date.now() / 1000);
        setShowSolution(false);
        setIsHintUsed(false);
        setSelectedAnswerText(null);
        setAddTopicStatus({ loading: false, added: false });
        setTempPoints(savedAnswer?.points_awarded ?? '');
        setChatHistory([]); // Reset AI mock history
    }, [currentProblemIndex, currentProblem, savedAnswer]);

    const maxTaskNumber = attemptData.problems.length;
    const dailyGoalProgress = 40; // Mock

    const getVisualLengthEstimate = (text) => {
        if (!text) return 0;
        const sanitizedText = text.replace(/\\[a-zA-Z]+|\$|{|}/g, '');
        return sanitizedText.length;
    };
    
    const gridClass = useMemo(() => {
        if (!currentProblem || currentProblem.is_open) return "grid-cols-1";
        let dynamicThreshold = width < 768 ? 15 : 40;
        const isAnyAnswerLong = currentProblem.answers.some(answer => getVisualLengthEstimate(answer) > dynamicThreshold);
        return isAnyAnswerLong ? "grid-cols-1" : "grid-cols-2";
    }, [currentProblem, width]);

    // --- MOCK HANDLERS ---

    const handleSaveSketch = (problemId, paths) => {
        setSketches(prev => ({
            ...prev,
            [problemId]: paths
        }));
    };

    const goToProblem = (index) => {
        if (isNavigating) return;
        if (index >= 0 && index < attemptData.problems.length) {
            setIsNavigating(true);
            setCurrentProblemIndex(index);
        }
    };

    // Mock działania "Dodaj do treningu"
    const handleAddSubfieldToTraining = async () => {
        if (addTopicStatus.added || addTopicStatus.loading) return;
        setAddTopicStatus({ loading: true, added: false });
        setTimeout(() => {
            setAddTopicStatus({ loading: false, added: true });
        }, 800);
    };

    // Lokalny zapis odpowiedzi
    const handleSaveAnswerLocal = (answerPayload) => {
        const newProblems = [...attemptData.problems];
        newProblems[currentProblemIndex] = {
            ...newProblems[currentProblemIndex],
            attempt_answer: {
                selected_answer: answerPayload.selected_answer,
                points_awarded: answerPayload.points_awarded,
                is_skipped: false
            }
        };
        setAttemptData({ ...attemptData, problems: newProblems });
    };

    const onSelectAnswer = (answerText) => {
        if (isAnswerChecked || isReviewMode) return;
        setSelectedAnswerText(answerText);
        const isSolved = answerText === currentProblem.true_answer;
        const points = isSolved ? 1 : 0;
        
        handleSaveAnswerLocal({
            selected_answer: answerText,
            points_awarded: points
        });
        
        // W tutorialu szybciej pokazujemy rozwiązanie
        setTimeout(() => setShowSolution(true), 600);
    };

    const handleSaveOpenAnswer = () => {
        if (isReviewMode) return;
        let points = parseInt(tempPoints, 10);
        const maxPoints = parseInt(currentProblem.max_points, 10);
        if (isNaN(points) || points < 0) points = 0;
        if (points > maxPoints) points = maxPoints;
        
        setTempPoints(points.toString());
        handleSaveAnswerLocal({
            selected_answer: null,
            points_awarded: points
        });
        setShowSolution(true);
    };

    const handleHint = () => {
        setIsHintUsed(true);
    };

    const handleLightbulbClick = () => {
        handleHint(); 
        setIsHintPopupOpen(true);
    };

    const handleFormulaClick = () => {
        handleHint(); 
    };

    const handleSolution = () => {
        if (isAnswerChecked) {
            setShowSolution(true);
        }
    };

    const handleCompleteAttempt = () => {
        if (window.confirm("To tylko wersja demonstracyjna. Czy chcesz zakończyć przeglądanie?")) {
            navigate('/start-exam');
        }
    };

    // --- AI MOCK ---
    const handleOpenChat = () => {
        setIsChatOpen(true);
    };

    const handleSendMessage = (msg) => {
        setChatHistory(prev => [...prev, { role: 'user', content: msg }]);
        setIsResponding(true);
        setTimeout(() => {
            setChatHistory(prev => [...prev, { role: 'assistant', content: currentProblem.ai_explanation || "W wersji pełnej tutaj otrzymasz szczegółowe wyjaśnienie oparte na Twoim błędzie." }]);
            setIsResponding(false);
        }, 1500);
    };

    // --- RENDEROWANIE (Identyczne jak w oryginale) ---

    const renderActionToolbar = () => {
        return (
            <div className="space-y-3 mt-4 pt-3 border-t border-gray-200">
                <button
                    onClick={handleOpenChat}
                    disabled={isResponding}
                    className="w-full px-4 py-2 bg-[#E09F2E] text-white font-apotek shadow-md tracking-widest text-xs lg:text-xs rounded-md hover:bg-yellow-600 disabled:bg-purple-300 font-thin"
                >
                    WYJAŚNIJ MI TO! (DEMO)
                </button>

                <div className="flex items-center gap-3 pt-2">
                    <span className="flex-grow border-t border-gray-200"></span>
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Utrwalanie
                    </span>
                    <span className="flex-grow border-t border-gray-200"></span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button 
                        onClick={() => setIsModalOpen(true)}
                        className="w-full px-3 py-2 bg-[#233855] text-slate-100 font-apotek uppercase shadow-lg tracking-widest text-xs lg:text-xs rounded-lg hover:bg-[#345081] disabled:bg-gray-300"
                    >
                        Rozwiąż podobne
                    </button>
                    
                    <button
                        onClick={handleAddSubfieldToTraining}
                        disabled={addTopicStatus.loading || addTopicStatus.added}
                        className={`w-full px-3 py-2 text-slate-100 font-apotek uppercase shadow-lg tracking-widest text-xs lg:text-xs rounded-lg transition-colors ${
                            addTopicStatus.added 
                                ? 'bg-[#007D00]' 
                                : 'bg-[#233855] hover:bg-[#345081] disabled:bg-gray-300'
                        }`}
                    >
                        {addTopicStatus.loading ? '...' : (addTopicStatus.added ? 'Dodano Temat!' : 'Dodaj do treningu dowolnego')}
                    </button>
                </div>
            </div>
        );
    };

    const renderOpenTaskAnswer = () => {
        const shouldShowSolution = showSolution || isReviewMode;
        const maxPointsInt = parseInt(currentProblem.max_points, 10);

        if (isAnswerChecked) {
            if (shouldShowSolution) {
                    return (
                        <div className="space-y-4">
                            <div className="w-full mt-4">
                                <SolutionStepper solutionText={currentProblem.solution || "Brak rozwiązania."} />
                            </div>
                            
                            {renderActionToolbar()}
                            
                            {currentProblem.criteria.length > 0 && (
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-4">
                                    <h3 className="font-semibold text-gray-700 mb-2">Kryteria Oceniania:</h3>
                                    <ul className="list-disc list-inside space-y-1 text-sm">
                                        {currentProblem.criteria.map((crit, idx) => (
                                            <li key={idx} className="flex gap-2 items-start">
                                                <strong className="whitespace-nowrap shrink-0">{parseInt(crit.points, 10)} pkt: </strong> 
                                                <div className="flex-grow">
                                                    <RenderMarkdownWithLatex text={crit.description} />
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="mt-4">
                                <label htmlFor="points" className="block text-sm font-medium text-gray-700">
                                    Twoje punkty (0 - {maxPointsInt}):
                                </label>
                                <select 
                                    id="points" name="points"
                                    value={savedAnswer?.points_awarded ?? ''}
                                    disabled={true} 
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 text-gray-500 sm:text-sm p-2"
                                >
                                    <option value="">Brak oceny</option>
                                    {Array.from({ length: maxPointsInt + 1 }, (_, i) => i).map(num => (
                                        <option key={num} value={num}>{num}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    );
            }
            else {
                return (
                    <div className="space-y-4 ">
                        <div className="mt-4">
                            <label htmlFor="points" className="block text-sm font-medium text-gray-700">
                                Twoje punkty (0 - {maxPointsInt}):
                            </label>
                            <select 
                                id="points" name="points"
                                value={savedAnswer?.points_awarded ?? ''}
                                disabled={true} 
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 text-gray-500 sm:text-sm p-2"
                            >
                                <option value="">Brak oceny</option>
                                {Array.from({ length: maxPointsInt + 1 }, (_, i) => i).map(num => (
                                    <option key={num} value={num}>{num}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            onClick={() => setShowSolution(true)}
                            className="w-full bg-[#E09F2E] text-white font-apotek uppercase shadow-md tracking-widest text-sm py-2 rounded-md hover:bg-yellow-600"
                        >
                            Pokaż rozwiązanie i kryteria
                        </button>

                        <div className=" lg:py-2"></div>
                    </div>
                );
            }
        }

        if (!showSolution && !isReviewMode) {
             return (
                <button
                    onClick={() => setShowSolution(true)} 
                    className="w-full bg-[#E09F2E] text-white font-apotek shadow-md tracking-widest text-sm py-2 rounded-md hover:bg-yellow-600"
                >
                    Pokaż rozwiązanie i oceń
                </button>
            );
        }

        return (
            <>
                <div className="w-full mt-4">
                    <SolutionStepper solutionText={currentProblem.solution || "Brak rozwiązania."} />
                </div>
                
                <div className="mt-4">
                    {renderActionToolbar()}
                </div>
                
                {currentProblem.criteria.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-4">
                        <h3 className="font-semibold text-gray-700 mb-2">Kryteria Oceniania:</h3>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                                    {currentProblem.criteria.map((crit, idx) => (
                                        <li key={idx} className="flex gap-2 items-start">
                                            <strong className="whitespace-nowrap shrink-0">{parseInt(crit.points, 10)} pkt: </strong> 
                                            <div className="flex-grow">
                                                <RenderMarkdownWithLatex text={crit.description} />
                                            </div>
                                        </li>
                                    ))}
                        </ul>
                    </div>
                )}

                <div className="mt-4">
                    <label htmlFor="points" className="block text-sm font-medium text-gray-700">
                        Wybierz swoje punkty (0 - {maxPointsInt}):
                    </label>
                    <div className="flex items-center gap-2 mt-1">
                        <select
                            id="points" name="points"
                            value={tempPoints} 
                            onChange={(e) => setTempPoints(e.target.value)}
                            disabled={isReviewMode} 
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 disabled:bg-gray-100 disabled:text-gray-500"
                        >
                            <option value="">Wybierz punkty...</option>
                            {Array.from({ length: maxPointsInt + 1 }, (_, i) => i).map(num => (
                                <option key={num} value={num}>{num}</option>
                            ))}
                        </select>
                        
                        {!isReviewMode && (
                            <button
                                onClick={handleSaveOpenAnswer}
                                className="bg-green-600 text-white font-apotek shadow-md tracking-widest text-sm py-2 px-4 rounded-md hover:bg-green-700"
                            >
                                Zapisz
                            </button>
                        )}
                    </div>
                </div>
            </>
        );
    };

    return (
        <PageTestPage
            showNav={false}
            isDemoMode={true}
            accountStatus={accountStatus}
            dailyGoalProgress={dailyGoalProgress}
        >
            <div className="fixed top-0 inset-x-0 bg-[#233855] text-white text-xs py-2 text-center z-[60] uppercase tracking-widest font-bold flex items-center justify-center gap-2 shadow-md">
                Tryb Samouczka - Dane nie są zapisywane
            </div>

            {/* --- Popupy --- */}
            
            
            <AiChatPopup
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
                chatHistory={chatHistory}
                onSendMessage={handleSendMessage}
                isResponding={isResponding}
                problemQuestion={currentProblem?.question}
                remainingCalls={99}
                maxCalls={100}
            />
            <TrainingProblemsModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                sourceProblemId={currentProblem.problem_id}
            />
            <AttemptSummaryModal 
                isOpen={!!summaryAttemptId}
                onClose={() => setSummaryAttemptId(null)}
                attemptId={summaryAttemptId}
            />

            {currentProblem && (
                <ScratchpadModal
                    isOpen={isScratchpadOpen}
                    onClose={() => setIsScratchpadOpen(false)}
                    problem={currentProblem}
                    initialPaths={sketches[currentProblem.problem_id]} 
                    onSave={handleSaveSketch}
                />
            )}

            <div className="flex flex-col max-w-2xl mx-auto w-full flex-grow px-4 lg:px-0 pt-8">
                
                <div className="w-full flex mt-[3vh] lg:mt-[8vh] mb-2">
                    <button 
                        onClick={() => navigate('/start-exam')}
                        className="text-gray-500 hover:text-gray-800 transition-colors flex items-center gap-1 text-sm font-medium"
                    >
                        <AiOutlineArrowLeft /> Wróć do listy arkuszy
                    </button>
                </div>
                
                {/* --- Przyciski Nawigacji GÓRNE --- */}
                {currentProblemIndex < attemptData.problems.length - 1 && (
                    <div className="w-full pt-4 flex justify-between items-center">
                        <button 
                            onClick={() => goToProblem(currentProblemIndex - 1)}
                            disabled={currentProblemIndex === 0}
                            className="flex lg:w-[8rem] justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] focus:bg-[#233855] disabled:bg-gray-300 focus:outline-none focus:ring-0 transition-colors"
                        >
                            WSTECZ
                        </button>
                        <button 
                            onClick={() => goToProblem(currentProblemIndex + 1)}
                            disabled={isNavigating}
                            className="flex lg:w-[8rem] justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] focus:bg-[#233855] disabled:bg-gray-300 focus:outline-none focus:ring-0 transition-colors"
                        >
                            DALEJ
                        </button>
                    </div>
                )}
                
                {currentProblemIndex === attemptData.problems.length - 1 && (
                    <div className="w-full pt-4 flex justify-between items-center">
                        <button 
                            onClick={() => goToProblem(currentProblemIndex - 1)}
                            disabled={currentProblemIndex === 0}
                            className="flex lg:w-[8rem] justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] focus:bg-[#233855] disabled:bg-gray-300 focus:outline-none focus:ring-0 transition-colors"
                        >
                            WSTECZ
                        </button>
                        
                        {!isReviewMode && (
                            <button
                                onClick={handleCompleteAttempt}
                                className="flex lg:w-[12rem] justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] focus:bg-[#233855] focus:outline-none focus:ring-0 transition-colors"
                            >
                                ZAKOŃCZ DEMO
                            </button>
                        )}
                    </div>
                )}


                {/* --- Główna Karta --- */}
                <div className="bg-white rounded-xl lg:rounded-[3rem] shadow-[4px_4px_12px_rgba(0,0,0,0.30)] border border-gray-200 py-6 px-5 lg:px-14 w-full mt-4 overflow-hidden relative"> 
                    
                    {currentProblem.hint && currentProblem.hint.trim() !== "" && (
                        <button
                            onClick={handleLightbulbClick}
                            className="absolute top-4 right-4 text-[#E09F2E] hover:text-[#b88226] hover:scale-110 transition-all p-2 z-10"
                            aria-label="Zobacz wskazówkę"
                        >
                            <FaRegLightbulb size={24} strokeWidth={1} className="drop-shadow-sm" />
                        </button>
                    )}

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentProblem.problem_id}
                            variants={problemTransitionVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            onAnimationComplete={() => setIsNavigating(false)}
                            className="w-full space-y-6"
                        >

                            <div className="w-full flex justify-center items-baseline flex-wrap gap-x-3 sm:gap-x-4 gap-y-2 text-xs sm:text-base font-apotek uppercase tracking-wide text-gray-700 lg:mt-[0.5vh]">
                                <span className="uppercase font-thin text-xs lg:text-xs text-[#233855]">
                                    {attemptData.sheet_name}
                                </span>
                                
                                <span className="text-gray-400 font-thin text-xs lg:text-xs ">|</span>
                                
                                <span className="font-thin text-xs lg:text-xs text-[#8A0000]">
                                    Zadanie {currentProblem.display_number || currentProblem.problem_number} / {maxTaskNumber}
                                </span>
                            </div>

                            {currentProblem.is_excluded && (
                                <div className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md text-center text-sm font-medium mb-4">
                                    ⚠️ To zadanie zostało wycofane z wymagań CKE do matury od 2025 roku. 
                                    Możesz je rozwiązać dla treningu, ale punkty nie wliczą się do wyniku końcowego.
                                </div>
                            )}

                            <div className="text-base lg:text-normal font-normal text-gray-900 text-center min-h-[50px] flex flex-col items-center justify-center lg:mt-0">
                                <RenderMarkdownWithLatex text={`(${parseInt(currentProblem.max_points, 10)} pkt) ${currentProblem.question}`} />
                            </div>

                            {currentProblem.image_link && (
                                <div className="flex justify-center py-2">
                                    <img 
                                            src={
                                                currentProblem.image_link.startsWith('http') 
                                                ? currentProblem.image_link 
                                                : `${process.env.REACT_APP_STATIC_FILES_SOURCE || ''}/images/${currentProblem.image_link}`
                                            } 
                                            alt="Ilustracja zadania" 
                                            className="max-w-full h-auto rounded" 
                                    />
                                </div>
                            )}

                            <div className="w-full">
                                {(() => {
                                    const hasLinkedFormulas = currentProblem.linked_formulas && currentProblem.linked_formulas.length > 0;
                                    
                                    if (!hasLinkedFormulas) return null;

                                    if (isHintUsed) {
                                        return (
                                                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                                <HintAndFormulas 
                                                    hintText={null} 
                                                    formulas={currentProblem.linked_formulas} 
                                                />
                                                </div>
                                        );
                                    } else {
                                        return (
                                                <button 
                                                    onClick={handleFormulaClick} 
                                                    className="group relative w-full flex items-center justify-between bg-white border border-gray-200 border-l-[4px] border-l-[#E09F2E] px-4 py-3 rounded-r-lg shadow-sm hover:shadow-md transition-all active:scale-[0.98] mb-4"
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className="text-[#233855] opacity-80 group-hover:opacity-100 transition-opacity">
                                                            <FaRegFileAlt size={22} />
                                                        </div>
                                                        
                                                        <div className="text-left">
                                                            <span className="block text-sm uppercase tracking-wider font-bold text-[#E09F2E] font-apotek">
                                                                Karta Wzorów 
                                                            </span>
                                                            <span className="block text-xs font-semibold text-[#233855] leading-tight">
                                                                Otwórz wzory do zadania
                                                            </span>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="text-[#233855] opacity-50 transform group-hover:translate-x-1 group-hover:opacity-100 transition-all">
                                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                                        </svg>
                                                    </div>
                                                </button>
                                        );
                                    }
                                })()}
                            </div>

                            {!currentProblem.is_open && (
                                <div className={`grid ${gridClass} gap-2 w-full`}>
                                    {currentProblem.answers.map((answer, index) => {
                                        const isCorrectAndChecked = isAnswerChecked && currentProblem.true_answer === answer;
                                        const isIncorrectSelected = isAnswerChecked && !isCorrectAndChecked && savedAnswer?.selected_answer === answer;
                                        let buttonClass = "flex items-center justify-center rounded-lg text-[0.8rem] text-slate-100 px-3 py-2 font-normal tracking-wider shadow-md transition-all duration-200 disabled:cursor-not-allowed min-h-[20px] w-full";
                                        if (isCorrectAndChecked) buttonClass += " bg-[#007D00] ring-2 ring-offset-2 ring-[#007D00]";
                                        else if (isIncorrectSelected) buttonClass += " bg-[#8A0000] opacity-90";
                                        else if (isAnswerChecked) buttonClass += " bg-[#233855] opacity-40";
                                        else buttonClass += " bg-[#233855] hover:bg-[#345081]";
                                        return (
                                            <button
                                                key={index}
                                                onClick={() => onSelectAnswer(answer)}
                                                disabled={isAnswerChecked || isReviewMode} 
                                                className={buttonClass}
                                            >
                                                <RenderMarkdownWithLatex text={answer} />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            
                            {!currentProblem.is_open && (
                                <div className="w-full">
                                    {(() => {
                                        if (showSolution || (isAnswerChecked && isReviewMode)) {
                                            return (
                                                <div className="w-full mt-6 relative animate-in fade-in zoom-in duration-300">
                                                        <SolutionStepper solutionText={currentProblem.solution || "Brak rozwiązania."} />
                                                        {renderActionToolbar()}
                                                </div>
                                            );
                                        }
                                        if (!showSolution) {
                                            return (
                                                <button
                                                    onClick={handleSolution} 
                                                    disabled={!isAnswerChecked} 
                                                    className="w-full text-xs lg:text-xs font-uppercase font-apotek shadow-md tracking-widest bg-[#F0F0F0] border border-gray-300 text-gray-600 hover:bg-gray-50 px-4 py-1.5 rounded-lg font-medium transition-colors lg:mb-[3vh] disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    ROZWIĄZANIE
                                                </button>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                            )}
                            
                            {currentProblem.is_open && renderOpenTaskAnswer()}
                            
                            <div className="mt-8 pt-4 border-t border-gray-100 w-full flex justify-center">
                                <ProblemStatusBadge problem={currentProblem} answer={savedAnswer} />
                            </div>

                        </motion.div>
                    </AnimatePresence>
                </div> 

                <div className="flex-grow" />
                <div className="h-[4vh] flex-shrink-0" />
            </div>

            <button
                onClick={() => setIsScratchpadOpen(true)}
                className="fixed bottom-12 right-4 z-40 bg-[#233855] text-white p-4 rounded-full shadow-[0_4px_14px_0_rgba(0,0,0,0.39)] hover:bg-[#345081] transition-transform hover:scale-105 active:scale-95 border-2 border-[#E09F2E]"
                aria-label="Otwórz brudnopis"
            >
                <FaPencilAlt size={20} />
            </button>
            
            <HintPopup 
                isOpen={isHintPopupOpen} 
                onClose={() => setIsHintPopupOpen(false)} 
                hintText={currentProblem.hint} 
            />

        </PageTestPage>
    );
};

export default TutorialExamResolver;